'use client';

import { useEffect, useState } from 'react';

export type UseScrollFadeHeaderOptions = {
  enabled: boolean;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  exitTargetRef?: React.RefObject<HTMLElement>;
  threshold?: number;
  useWindowScroll?: boolean;
};

export type UseScrollFadeHeaderReturn = {
  backgroundOpacity: number;
};

export const useScrollFadeHeader = ({
  enabled = false,
  scrollContainerRef,
  exitTargetRef,
  threshold = 50,
  useWindowScroll = false,
}: UseScrollFadeHeaderOptions): UseScrollFadeHeaderReturn => {
  const [backgroundOpacity, setBackgroundOpacity] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setBackgroundOpacity(0);
      return;
    }

    const handleScroll = () => {
      const scrollY = useWindowScroll ? window.scrollY : scrollContainerRef?.current?.scrollTop;

      if (scrollY == null) return;

      const shouldShowHeader = exitTargetRef?.current
        ? exitTargetRef.current.getBoundingClientRect().bottom <= 0
        : scrollY > threshold;
      const newOpacity = shouldShowHeader ? 1 : 0;

      setBackgroundOpacity((current) => {
        if (current !== newOpacity) {
          return newOpacity;
        }
        return current;
      });
    };

    if (useWindowScroll) {
      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }

    const container = scrollContainerRef?.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [enabled, exitTargetRef, scrollContainerRef, threshold, useWindowScroll]);

  return { backgroundOpacity };
};
